import { useEffect, useMemo, useRef } from "react"

/**
 * Hook that delays calling a function until it stops being called
 * @param callback - The function to debounce
 * @param delay - Delay in milliseconds (default: 500ms)
 * @returns The debounced function, with `cancel` to drop a pending call
 */
export function useDebouncedCallback<TArgs extends unknown[]>(
  callback: (...args: TArgs) => void,
  delay: number = 500,
) {
  const callbackRef = useRef(callback)
  const timeoutRef = useRef<number>(undefined)

  // Always run the latest callback without re-creating the debounced function
  useEffect(() => {
    callbackRef.current = callback
  })

  const debouncedCallback = useMemo(() => {
    const cancel = () => window.clearTimeout(timeoutRef.current)
    const run = (...args: TArgs) => {
      cancel()
      timeoutRef.current = window.setTimeout(
        () => callbackRef.current(...args),
        delay,
      )
    }

    return Object.assign(run, { cancel })
  }, [delay])

  // A pending call shouldn't run after the component unmounts
  useEffect(() => debouncedCallback.cancel, [debouncedCallback])

  return debouncedCallback
}
